/* eslint-disable react/prop-types */
import { useMemo, useEffect, useState } from "react";

// prop-types is a library for typechecking of props
import PropTypes from "prop-types";

// react-table components
import { useTable, usePagination, useGlobalFilter, useAsyncDebounce, useSortBy } from "react-table";

// components
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftSelect from "components/SoftSelect";
import SoftInput from "components/SoftInput";

// example components
import { Stack } from "@mui/material";
import { Link } from "react-router-dom";
import SoftButton from "components/SoftButton";
import TableCommon from "components/TableCommon";

function DataTable({
  table,
  pagination,
  isSorted,
  noEndBorder,
  setQuery,
  checkCreate,
  query,
  paginationData,
  setPaginationData,
  loading,
}) {
  const columns = useMemo(() => table.columns, [table]);
  const data = useMemo(() => table.rows, [table]);

  const tableInstance = useTable(
    { columns, data, initialState: { pageIndex: 0 } },
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  const {
    setPageSize,
    setGlobalFilter,
    state: { globalFilter },
  } = tableInstance;

  useEffect(() => setPageSize(paginationData.size), []);

  const [search, setSearch] = useState(globalFilter);

  const onSearchChange = useAsyncDebounce((value) => {
    setQuery((prev) => ({ ...prev, searchBy: value }));
  }, 100);

  const dateNow = new Date();

  return (
    <>
      <SoftBox
        display="flex"
        flexDirection="column"
        justifyContent="space-between"
        alignItems="flex-start"
        p={3}
        pb={1}
      >
        <Stack spacing={1} direction="row" mb={3}>
          {checkCreate && (
            <Link to="/templates/register-own-shares-purchased/create">
              <SoftButton variant="gradient" color="dark" size="small">
                Tạo Sổ Chi Tiết Cổ Phiếu Mua Lại Mới
              </SoftButton>
            </Link>
          )}
        </Stack>
        <SoftBox lineHeight={1}>
          <Stack spacing={2} direction="row">
            <SoftBox display="flex" flexDirection="column">
              <SoftTypography component="label" variant="caption" fontWeight="bold" mb={0.5}>
                Tìm kiếm
              </SoftTypography>
              <SoftBox width="12rem">
                <SoftInput
                  placeholder="Search..."
                  value={search}
                  onChange={({ currentTarget }) => {
                    setSearch(currentTarget.value);
                    onSearchChange(currentTarget.value);
                  }}
                />
              </SoftBox>
            </SoftBox>
          </Stack>
        </SoftBox>
      </SoftBox>

      <TableCommon
        loading={loading}
        query={query}
        setQuery={setQuery}
        paginationData={paginationData}
        setPaginationData={setPaginationData}
        table={table}
        isSorted={isSorted}
        noEndBorder={noEndBorder}
        pagination={pagination}
        tableInstance={tableInstance}
      />
    </>
  );
}

DataTable.defaultProps = {
  entriesPerPage: { defaultValue: 10, entries: [5, 10, 15, 20, 25] },
  showTotalEntries: true,
  pagination: { variant: "gradient", color: "info" },
  isSorted: true,
  noEndBorder: false,
};

// Typechecking props for the DataTable
DataTable.propTypes = {
  query: PropTypes.any,
  setQuery: PropTypes.any,
  entriesPerPage: PropTypes.oneOfType([
    PropTypes.shape({
      defaultValue: PropTypes.number,
      entries: PropTypes.arrayOf(PropTypes.number),
    }),
    PropTypes.bool,
  ]),
  showTotalEntries: PropTypes.bool,
  table: PropTypes.objectOf(PropTypes.array).isRequired,
  pagination: PropTypes.shape({
    variant: PropTypes.oneOf(["contained", "gradient"]),
    color: PropTypes.oneOf([
      "primary",
      "secondary",
      "info",
      "success",
      "warning",
      "error",
      "dark",
      "light",
    ]),
  }),
  isSorted: PropTypes.bool,
  noEndBorder: PropTypes.bool,
};

export default DataTable;
